import React, { useMemo, useRef, useState } from "react";
import { Box, Flex, Text, Icon, Input, InputGroup, InputLeftElement, Button } from "@chakra-ui/react";
import { HiOutlineMagnifyingGlass, HiOutlineWrenchScrewdriver } from "react-icons/hi2";
import { c } from "../classroomColors";
import { useClassroomToast } from "../classroomToast";
import { buildMatcher, haystack } from "../../Components/searchMatch";
import CourseOutline from "./CourseOutline";
import CourseProgress from "./CourseProgress";
import LessonNav from "./LessonNav";
import LessonPage from "./LessonPage";
import RenameDialog from "./RenameDialog";
import ConfirmDelete from "./ConfirmDelete";
import { stripHtml } from "./contentTokens";

/**
 * The course-content tab: an outline on the left, the lesson on the right.
 *
 * Built so a teacher can lay a whole course out without leaving the page —
 * modules and lessons are added in place, and the lesson body is edited where
 * it is read rather than in a separate dialog.
 */
const CourseContent = ({ content, courseGuid, isInstructor, isDark, onOpenAdvanced }) => {
  const {
    modules, error, busy,
    addModule, addLesson, rename, saveBody, remove, reorder, upload, removeAttachment,
  } = content;

  const toast = useClassroomToast(isDark);
  const [selectedGuid, setSelectedGuid] = useState(null);
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const lastCreated = useRef(null);

  // Flatten once so selection, search and the module label all read from the
  // same place rather than each walking the tree.
  const flat = useMemo(() => {
    const out = [];
    (modules || []).forEach((mod) => {
      out.push({ node: mod, moduleName: null, isModule: true });
      (mod.child_segments || []).forEach((lesson) =>
        out.push({ node: lesson, moduleName: mod.name, isModule: false })
      );
    });
    return out;
  }, [modules]);

  const selected = flat.find((x) => x.node.guid === selectedGuid) || null;

  // Stepping through the course means stepping through lessons; a module is a
  // heading, not something you read.
  const lessonSteps = useMemo(() => flat.filter((x) => !x.isModule), [flat]);
  const stepIndex = lessonSteps.findIndex((x) => x.node.guid === selectedGuid);
  const prevStep = stepIndex > 0 ? lessonSteps[stepIndex - 1] : null;
  const nextStep = stepIndex >= 0 && stepIndex < lessonSteps.length - 1 ? lessonSteps[stepIndex + 1] : null;

  const visibleModules = useMemo(() => {
    if (!modules) return null;
    const matches = buildMatcher(query);
    if (!query.trim()) return modules;
    // A module stays when it matches, or when any of its lessons do — so a
    // search never hides the heading the result sits under.
    return modules
      .map((mod) => {
        const lessons = (mod.child_segments || []).filter((l) =>
          matches(haystack([l.name, stripHtml(l.overview_html).slice(0, 400)]))
        );
        const modMatches = matches(haystack([mod.name, stripHtml(mod.overview_html).slice(0, 400)]));
        if (!modMatches && lessons.length === 0) return null;
        return { ...mod, child_segments: modMatches ? mod.child_segments : lessons };
      })
      .filter(Boolean);
  }, [modules, query]);

  const handleAddModule = async (name) => {
    const created = await addModule(name);
    lastCreated.current = created?.guid || null;
    toast({ title: "Module added", description: name, status: "success", duration: 2000 });
  };

  const handleAddLesson = async (moduleGuid, name) => {
    const created = await addLesson(moduleGuid, name);
    if (created?.guid) setSelectedGuid(created.guid);
    toast({ title: "Lesson added", description: name, status: "success", duration: 2000 });
  };

  const handleRename = async (node, name) => {
    await rename(node.guid, name);
    toast({ title: "Renamed", status: "success", duration: 2000 });
  };

  const handleDelete = async () => {
    const { node } = deleting;
    try {
      await remove(node.guid);
      if (selectedGuid === node.guid) setSelectedGuid(null);
      toast({ title: "Deleted", description: node.name, status: "success", duration: 2200 });
    } catch (e) {
      console.error(e);
      toast({ title: "Couldn't delete that", description: "Please try again.", status: "error" });
    } finally {
      setDeleting(null);
    }
  };

  /** Move one item by one place. The whole affected list is renumbered, since
   *  positions are only meaningful relative to their siblings. */
  const handleMove = async (node, delta, parentGuid) => {
    const siblings = parentGuid
      ? (modules.find((m) => m.guid === parentGuid)?.child_segments || [])
      : modules;
    const from = siblings.findIndex((s) => s.guid === node.guid);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= siblings.length) return;

    const next = [...siblings];
    [next[from], next[to]] = [next[to], next[from]];
    await reorder(
      next.map((s, i) => ({
        guid: s.guid,
        parent_course_segment_guid: parentGuid,
        position: i,
      }))
    );
  };

  const handleSaveBody = async (guid, html) => {
    await saveBody(guid, html);
    toast({ title: "Lesson saved", status: "success", duration: 2000 });
  };

  const handleUpload = async (guid, files) => {
    try {
      await upload(guid, files);
      toast({
        title: files.length === 1 ? "File added" : `${files.length} files added`,
        status: "success",
        duration: 2200,
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Upload failed", description: "Please try again.", status: "error" });
    }
  };

  const handleRemoveAttachment = async (attachmentGuid) => {
    try {
      await removeAttachment(attachmentGuid);
      toast({ title: "Removed", status: "success", duration: 1800 });
    } catch (e) {
      console.error(e);
      toast({ title: "Couldn't remove that", status: "error" });
    }
  };

  return (
    <Flex gap="28px" align="flex-start" direction={{ base: "column", lg: "row" }}>
      {/* outline */}
      <Box width={{ base: "100%", lg: "364px" }} flexShrink={0}>
        <Flex justify="space-between" align="center" mb="12px" gap="8px">
          <Text fontSize="14px" fontWeight="600" color={c.text}>
            Course content
          </Text>
          {isInstructor && onOpenAdvanced && (
            <Button
              leftIcon={<Icon as={HiOutlineWrenchScrewdriver} boxSize="13px" />}
              size="xs" h="26px" px="9px" variant="ghost" borderRadius="7px"
              color={c.textMuted} fontSize="11.5px" fontWeight="400"
              _hover={{ bg: c.hover, color: c.text }}
              onClick={onOpenAdvanced}
            >
              Bulk edit
            </Button>
          )}
        </Flex>

        {(modules?.length > 2 || query) && (
          <InputGroup size="sm" mb="12px">
            <InputLeftElement pointerEvents="none" h="36px">
              <Icon as={HiOutlineMagnifyingGlass} color={c.textMuted} boxSize="14px" />
            </InputLeftElement>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the course"
              h="36px" borderRadius="9px" bg={c.inputBg}
              border="1px solid" borderColor={c.inputBorder} color={c.text} fontSize="13px"
              _placeholder={{ color: c.textMuted }}
              _focus={{ borderColor: c.textMuted, boxShadow: "none" }}
            />
          </InputGroup>
        )}

        {!error && modules?.length > 0 && (
          <CourseProgress
            modules={modules}
            isInstructor={isInstructor}
            isDark={isDark}
            onJump={(lesson) => setSelectedGuid(lesson.guid)}
          />
        )}

        {error ? (
          <Text fontSize="13px" color={c.textSub} py="20px" textAlign="center">
            {error}
          </Text>
        ) : visibleModules && visibleModules.length === 0 && query ? (
          <Text fontSize="12.5px" color={c.textMuted} py="20px" textAlign="center">
            Nothing matches “{query}”.
          </Text>
        ) : (
          <CourseOutline
            modules={visibleModules}
            selectedGuid={selectedGuid}
            onSelect={(node) => setSelectedGuid(node.guid)}
            isInstructor={isInstructor}
            isDark={isDark}
            onAddModule={handleAddModule}
            onAddLesson={handleAddLesson}
            onRename={(node) => setRenaming(node)}
            onDelete={(node, kind) => setDeleting({ node, kind })}
            onMove={handleMove}
          />
        )}
      </Box>

      {/* lesson */}
      <Box flex="1" minW={0} width="100%">
        <LessonPage
          segment={selected?.node || null}
          moduleName={selected?.moduleName}
          isInstructor={isInstructor}
          isDark={isDark}
          busy={busy}
          onSaveBody={handleSaveBody}
          onUpload={handleUpload}
          onRemoveAttachment={handleRemoveAttachment}
        />
        {selected && !selected.isModule && (
          <LessonNav prev={prevStep} next={nextStep} onGo={(node) => setSelectedGuid(node.guid)} />
        )}
      </Box>

      <RenameDialog
        node={renaming}
        isDark={isDark}
        onClose={() => setRenaming(null)}
        onSubmit={handleRename}
      />
      <ConfirmDelete
        target={deleting}
        isDark={isDark}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </Flex>
  );
};

export default CourseContent;
